import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  HiOutlineBriefcase,
  HiOutlineClipboardDocumentCheck,
  HiOutlineUsers,
  HiOutlineSwatch,
  HiOutlineCreditCard,
  HiOutlineChatBubbleLeftRight,
  HiOutlineUserGroup,
  HiOutlineMusicalNote,
  HiOutlineFire,
} from "react-icons/hi2";
import {
  MdSportsMartialArts,
  MdPool,
  MdSportsSoccer,
} from "react-icons/md";
import logo from "../../../assets/logo.png";
import L from "../../../constants/baysportLabels";
import { LegalFooterLinks } from "../../Legal/LegalDocLink";
import { TRIAL_DAYS } from "../../../constants/trial";
import {
  buildLandingPriceHighlights,
  PLAN_POSITIONING,
  LANDING_PLANS_FALLBACK,
} from "../../../constants/planMarketing";
import platformAPI from "../../../services/platformApi";
import { getDashboardPath } from "../../../utils/authRedirect";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { hasAdminAccess } from "../../../utils/roles";
import { trackTryClick } from "../../../analytics/metrikaGoals";
import { yandexMetrikaParams } from "../../../analytics/yandexMetrika";
import {
  CRM_LANDING_PAGE_TITLE,
  CRM_LANDING_PAGE_DESCRIPTION,
  CRM_LANDING_PAGE_KEYWORDS,
  CRM_LANDING_PATH,
  LANDING_OG_IMAGE,
  LANDING_OG_IMAGE_ALT,
  buildCrmLandingJsonLd,
} from "../HomeLanding/landingSeo";
import { SITE_ORIGIN } from "../../../config/site";
import Preloader from "../../common/Preloader";
import Seo from "../../Seo";
import s from "./avitoLanding.module.css";

function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

const NAV = [
  { id: "problems", label: "Проблемы" },
  { id: "benefits", label: "Возможности" },
  { id: "audience", label: "Кому подходит" },
  { id: "pricing", label: "Тарифы" },
];

const PROBLEMS = [
  {
    title: "Хаос между тренерами",
    text: "У каждого своя тетрадь и чат. Владелец не видит, кто ходил и у кого заканчивается абонемент.",
  },
  {
    title: "Родители пишут в мессенджер",
    text: "«Была ли тренировка?», «Сколько занятий осталось?» — одни и те же вопросы каждый день.",
  },
  {
    title: "Должники теряются в Excel",
    text: "Оплаты и пакеты живут в таблице. Кто не продлил — узнаёте поздно.",
  },
];

const BENEFITS = [
  {
    art: "journal",
    title: "Журнал и абонементы в одном месте",
    text: "Тренер отметил группу — занятие списалось. Остаток виден клубу и родителю.",
  },
  {
    art: "brand",
    title: "Кабинеты под брендом клуба",
    text: "Родители и тренеры работают в вашем оформлении — под брендом клуба.",
  },
  {
    art: "pay",
    title: "Онлайн-оплата абонементов",
    text: "Подключите эквайринг — родители платят картой в кабинете, деньги на ваш счёт.",
  },
];

function BenefitArt({ kind }) {
  if (kind === "brand") {
    return (
      <div className={`${s.benefitArt} ${s.benefitBrand}`} aria-hidden>
        <div className={s.benefitPanel}>
          <div className={s.benefitBrandTop}>
            <i />
            <b>Ваш клуб</b>
          </div>
          <span className={s.benefitBar} />
          <span className={`${s.benefitBar} ${s.benefitBarShort}`} />
          <em>Кабинет родителя</em>
        </div>
      </div>
    );
  }
  if (kind === "pay") {
    return (
      <div className={`${s.benefitArt} ${s.benefitPay}`} aria-hidden>
        <div className={s.benefitPanel}>
          <div className={s.benefitPanelHead}>Абонемент</div>
          <strong>1 490 ₽</strong>
          <div className={s.benefitPayBtn}>Оплатить</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`${s.benefitArt} ${s.benefitJournal}`} aria-hidden>
      <div className={s.benefitPanel}>
        <div className={s.benefitPanelHead}>Журнал · сегодня</div>
        <div className={s.benefitRows}>
          <span>
            <i>✓</i>
            <b />
          </span>
          <span>
            <i>✓</i>
            <b />
          </span>
          <span className={s.benefitRowOff}>
            <i />
            <b />
          </span>
        </div>
      </div>
    </div>
  );
}

const AUDIENCE = [
  {
    id: "team",
    tone: "green",
    Icon: HiOutlineUserGroup,
    title: "Клуб с несколькими тренерами",
    text: "Единый журнал, роли и кабинет владельца — без пересылки Excel между сменами.",
    tags: ["Роли", "Общий журнал", "Владелец"],
  },
  {
    id: "martial",
    tone: "red",
    Icon: MdSportsMartialArts,
    title: "Клуб единоборств",
    text: "Группы по поясам и возрастам, посещения и абонементы — в одном кабинете для всей команды.",
    tags: ["Пояса", "Группы", "Абонементы"],
  },
  {
    id: "dance",
    tone: "pink",
    Icon: HiOutlineMusicalNote,
    title: "Студия танцев",
    text: "Направления, расписание и кабинет родителей: кто был на репетиции и сколько занятий осталось.",
    tags: ["Направления", "Расписание", "Родители"],
  },
  {
    id: "fitness",
    tone: "lime",
    Icon: HiOutlineFire,
    title: "Фитнес и групповые занятия",
    text: "Пакеты, списания и запись на занятия. Клиент видит остаток в кабинете, не в переписке.",
    tags: ["Пакеты", "Списания", "Запись"],
  },
  {
    id: "swim",
    tone: "blue",
    Icon: MdPool,
    title: "Бассейн и водные секции",
    text: "Смены тренеров, группы и абонементы в одном месте — удобно владельцу и родителям.",
    tags: ["Смены", "Группы", "Абонементы"],
  },
  {
    id: "football",
    tone: "purple",
    Icon: MdSportsSoccer,
    title: "Футбол и игровые виды",
    text: "Составы групп, посещаемость и продления. Родители меньше пишут «будет ли тренировка».",
    tags: ["Составы", "Посещения", "Продления"],
  },
];

const CAPABILITIES = [
  {
    title: "Для владельца",
    Icon: HiOutlineBriefcase,
    tone: "owner",
    items: ["Финансы и абонементы", "Несколько тренеров", "Отчёты по клубу"],
  },
  {
    title: "Для тренера",
    Icon: HiOutlineClipboardDocumentCheck,
    tone: "trainer",
    items: ["Журнал на телефоне", "Свои группы", "Отметки на занятии"],
  },
  {
    title: "Для родителей",
    Icon: HiOutlineUsers,
    tone: "parents",
    items: ["Расписание", "Посещения", "Оплата абонемента"],
  },
  {
    title: "Под вашим брендом",
    Icon: HiOutlineSwatch,
    tone: "brand",
    items: ["Логотип и цвета", "Свой кабинет на телефоне", "Оформление клуба"],
  },
  {
    title: "Оплаты",
    Icon: HiOutlineCreditCard,
    tone: "pay",
    items: ["ЮKassa или T-Bank", "Деньги на счёт клуба", "Без ручных переводов"],
  },
  {
    title: "Коммуникации",
    Icon: HiOutlineChatBubbleLeftRight,
    tone: "chat",
    items: ["Чат секции", "Соцсеть клуба", "Новости и лента"],
  },
];

const FAQ = [
  {
    q: "Чем Бай.Спорт лучше Excel и WhatsApp?",
    a: "В таблицах и чатах данные разъезжаются: кто ходил, у кого закончился абонемент, кто должен — никто не видит целиком. В Бай.Спорт журнал, пакеты и кабинет родителей связаны: отметили занятие — остаток обновился сразу.",
  },
  {
    q: "У нас несколько тренеров — это подойдёт?",
    a: "Да. У каждого тренера свои группы и журнал, у владельца — общая картина по клубу: посещения, абонементы и оплаты. Не нужно пересылать Excel между сменами.",
  },
  {
    q: "Что получат родители?",
    a: "Кабинет на телефоне под брендом вашего клуба: расписание, кто был на тренировке, сколько занятий осталось и оплата абонемента картой — без бесконечных сообщений «сколько осталось?».",
  },
  {
    q: "Можно ли принимать оплату за абонементы онлайн?",
    a: "Да. Подключаете ЮKassa или T-Bank — родители платят в кабинете, деньги приходят на счёт клуба. Ручные переводы и «забыл оплатить» сокращаются.",
  },
  {
    q: "Сложно ли внедрить? Нужны программисты?",
    a: "Нет. Регистрируете клуб, добавляете группы и тренеров, открываете журнал — без кода и без своего сервера. На старт карту указывать не нужно.",
  },
  {
    q: "Сколько стоит и что входит в триал?",
    a: `Первые ${TRIAL_DAYS} дней — полный доступ бесплатно. Дальше от 790 ₽ в месяц. Для клуба с командой обычно берут тариф «Расширенный»: несколько тренеров, роли и отчёты.`,
  },
];

export default function AvitoCrmLanding() {
  const { isAuth, role, tenant } = useSelector((state) => state.auth);
  const hasClub = isAuth && tenant?.subdomain && hasAdminAccess(role);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [activeNav, setActiveNav] = useState("problems");
  const [audienceIdx, setAudienceIdx] = useState(0);
  const [mobileAudienceIdx, setMobileAudienceIdx] = useState(0);
  const audienceScrollRef = useRef(null);

  useEffect(() => {
    yandexMetrikaParams({ landing: "crm" });
  }, []);

  useEffect(() => {
    platformAPI
      .getPublicPlans()
      .then((r) => {
        const loaded = Array.isArray(r?.plans) ? r.plans : [];
        setPlans(loaded.length ? loaded : LANDING_PLANS_FALLBACK);
      })
      .catch(() => setPlans(LANDING_PLANS_FALLBACK))
      .finally(() => setPlansLoading(false));
  }, []);

  useEffect(() => {
    const ids = NAV.map((item) => item.id);
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!elements.length) return undefined;

    const visibility = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibility.set(entry.target.id, entry.intersectionRatio);
        });
        let bestId = ids[0];
        let bestRatio = -1;
        ids.forEach((id) => {
          const ratio = visibility.get(id) || 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        if (bestRatio > 0) setActiveNav(bestId);
      },
      {
        root: null,
        rootMargin: "-18% 0px -55% 0px",
        threshold: [0, 0.2, 0.45, 0.7, 1],
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const featuredCode = useMemo(() => "pro", []);
  const onTry = () => trackTryClick({ landing: "crm" });

  const shiftAudience = (dir) => {
    setAudienceIdx((prev) => {
      const next = prev + dir;
      if (next < 0) return AUDIENCE.length - 1;
      if (next >= AUDIENCE.length) return 0;
      return next;
    });
  };

  const scrollMobileAudience = (dir) => {
    const el = audienceScrollRef.current;
    if (!el) return;
    const next = Math.min(
      AUDIENCE.length - 1,
      Math.max(0, mobileAudienceIdx + dir)
    );
    const card = el.children[next];
    if (card) {
      card.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    }
    setMobileAudienceIdx(next);
  };

  const onMobileAudienceScroll = () => {
    const el = audienceScrollRef.current;
    if (!el) return;
    const cardWidth = el.children[0]?.getBoundingClientRect().width || 1;
    const gap = 12;
    const idx = Math.round(el.scrollLeft / (cardWidth + gap));
    setMobileAudienceIdx(Math.min(AUDIENCE.length - 1, Math.max(0, idx)));
  };

  const visibleAudience = [
    AUDIENCE[audienceIdx % AUDIENCE.length],
    AUDIENCE[(audienceIdx + 1) % AUDIENCE.length],
    AUDIENCE[(audienceIdx + 2) % AUDIENCE.length],
  ];

  return (
    <div className={s.page}>
      <Seo
        title={CRM_LANDING_PAGE_TITLE}
        description={CRM_LANDING_PAGE_DESCRIPTION}
        keywords={CRM_LANDING_PAGE_KEYWORDS}
        canonicalUrl={`${SITE_ORIGIN}${CRM_LANDING_PATH}`}
        ogImage={LANDING_OG_IMAGE}
        ogImageAlt={LANDING_OG_IMAGE_ALT}
        jsonLd={buildCrmLandingJsonLd(FAQ)}
      />

      {hasClub ? (
        <div className={s.sessionBar}>
          <span>
            Вы вошли в <strong>{tenant.name || tenant.subdomain}</strong>
          </span>
          <a
            href={buildClinicPortalUrl(tenant.subdomain, getDashboardPath(role))}
            className={`${s.btn} ${s.btnDark}`}
          >
            Открыть кабинет
          </a>
        </div>
      ) : null}

      <div className={s.shell}>
        <header className={s.nav}>
          <Link to="/" className={s.navBrand}>
            <img src={logo} alt="" className={s.navLogo} />
            <span>{L.productName}</span>
          </Link>
          <nav className={s.navLinks} aria-label="Разделы">
            {NAV.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={activeNav === item.id ? s.navOn : undefined}
                onClick={() => setActiveNav(item.id)}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className={s.navActions}>
            <Link
              to="/register-clinic"
              className={`${s.btn} ${s.btnLime}`}
              onClick={onTry}
            >
              Попробовать
            </Link>
            <Link to="/account/login" className={`${s.btn} ${s.btnGhostDark}`}>
              Войти
            </Link>
          </div>
        </header>

        <section className={s.hero}>
          <div className={s.heroCopy}>
            <p className={s.heroEyebrow}>Для клуба с несколькими тренерами</p>
            <h1 className={s.heroTitle}>
              CRM для спортивного клуба вместо{" "}
              <span className={s.heroMark}>Excel и чатов</span>
            </h1>
            <p className={s.heroLead}>
              Журнал, абонементы, кабинеты тренеров и родителей — под вашим
              брендом. Без разработчиков.
            </p>
            <div className={s.heroStats}>
              <div>
                <strong>{TRIAL_DAYS} дней</strong>
                <span>полный доступ бесплатно</span>
              </div>
              <div>
                <strong>от 790 ₽</strong>
                <span>дальше по тарифу клуба</span>
              </div>
              <div>
                <strong>без кода</strong>
                <span>запуск за один вечер</span>
              </div>
            </div>
            <Link
              to="/register-clinic"
              className={`${s.btn} ${s.btnLight} ${s.btnXl}`}
              onClick={onTry}
            >
              Попробовать {TRIAL_DAYS} дней бесплатно
            </Link>
          </div>
          <div className={s.heroVisual} aria-hidden>
            <div className={s.heroGlow} />
            <div className={s.heroLaptop}>
              <div className={s.heroScreen}>
                <b />
                <b />
                <b />
              </div>
            </div>
            <div className={`${s.floatCard} ${s.floatA}`}>Журнал</div>
            <div className={`${s.floatCard} ${s.floatB}`}>Абонемент</div>
            <div className={`${s.floatCard} ${s.floatC}`}>Родители</div>
          </div>
        </section>
      </div>

      <section id="problems" className={s.section}>
        <h2 className={s.sectionTitle}>
          Почему клубу сложно жить на тетрадях и Excel?
        </h2>
        <div className={s.problemGrid}>
          {PROBLEMS.map((item) => (
            <article key={item.title} className={s.problemCard}>
              <span className={s.problemIcon} aria-hidden>
                ↓
              </span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="benefits" className={s.section}>
        <h2 className={s.sectionTitle}>
          Держите клуб в одном месте — когда команда уже растёт
        </h2>
        <div className={s.benefitGrid}>
          {BENEFITS.map((item) => (
            <article key={item.title} className={s.benefitCard}>
              <BenefitArt kind={item.art} />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="audience" className={s.darkBand}>
        <h2 className={s.darkTitle}>Кому подходит Бай.Спорт</h2>

        <div className={s.audienceMobile}>
          <div
            className={s.audienceScroll}
            ref={audienceScrollRef}
            onScroll={onMobileAudienceScroll}
          >
            {AUDIENCE.map((item) => {
              const Icon = item.Icon;
              return (
                <article
                  key={item.id}
                  className={`${s.audienceCard} ${s[`aud_${item.tone}`]}`}
                >
                  <div className={s.audienceIcon} aria-hidden>
                    <Icon />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <div className={s.audienceTags}>
                    {item.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
          <div className={s.audienceControls}>
            <button
              type="button"
              className={s.carouselBtn}
              aria-label="Назад"
              onClick={() => scrollMobileAudience(-1)}
              disabled={mobileAudienceIdx === 0}
            >
              ‹
            </button>
            <div className={s.audienceDots} aria-hidden>
              {AUDIENCE.map((item, i) => (
                <i
                  key={item.id}
                  className={i === mobileAudienceIdx ? s.audienceDotOn : undefined}
                />
              ))}
            </div>
            <button
              type="button"
              className={s.carouselBtn}
              aria-label="Вперёд"
              onClick={() => scrollMobileAudience(1)}
              disabled={mobileAudienceIdx >= AUDIENCE.length - 1}
            >
              ›
            </button>
          </div>
        </div>

        <div className={s.audienceRow}>
          <button
            type="button"
            className={s.carouselBtn}
            aria-label="Назад"
            onClick={() => shiftAudience(-1)}
          >
            ‹
          </button>
          <div className={s.audienceGrid}>
            {visibleAudience.map((item) => {
              const Icon = item.Icon;
              return (
                <article
                  key={item.id}
                  className={`${s.audienceCard} ${s[`aud_${item.tone}`]}`}
                >
                  <div className={s.audienceIcon} aria-hidden>
                    <Icon />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <div className={s.audienceTags}>
                    {item.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
          <button
            type="button"
            className={s.carouselBtn}
            aria-label="Вперёд"
            onClick={() => shiftAudience(1)}
          >
            ›
          </button>
        </div>
      </section>

      <section className={s.darkBand}>
        <h2 className={s.darkTitle}>Что получает клуб</h2>
        <div className={s.capGrid}>
          {CAPABILITIES.map((item) => {
            const Icon = item.Icon;
            return (
              <article key={item.title} className={s.capCard}>
                <div className={`${s.capArt} ${s[`cap_${item.tone}`]}`} aria-hidden>
                  <Icon />
                </div>
                <h3>{item.title}</h3>
                <ul>
                  {item.items.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      <section id="pricing" className={s.section}>
        <h2 className={s.sectionTitle}>Тарифы</h2>
        <p className={s.sectionLead}>
          Первые {TRIAL_DAYS} дней — полный доступ. Карту указывать не нужно.
        </p>
        {plansLoading ? (
          <Preloader variant="inline" label="Загрузка тарифов…" />
        ) : (
          <div className={s.pricing}>
            {plans.map((plan) => (
              <article
                key={plan.id}
                className={`${s.priceCard} ${
                  plan.code === featuredCode ? s.priceCardOn : ""
                }`}
              >
                {plan.badge ? <span className={s.badge}>{plan.badge}</span> : null}
                <h3>{plan.name}</h3>
                {PLAN_POSITIONING[plan.code] ? (
                  <p className={s.pricePos}>{PLAN_POSITIONING[plan.code]}</p>
                ) : null}
                <p className={s.priceAmount}>
                  {formatRub(plan.monthly_price_rub)} ₽
                  <small>/ мес</small>
                </p>
                <ul>
                  {buildLandingPriceHighlights(plan.code).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <Link
                  to="/register-clinic"
                  className={`${s.btn} ${
                    plan.code === featuredCode ? s.btnDark : s.btnGhost
                  }`}
                  onClick={onTry}
                >
                  {TRIAL_DAYS} дней бесплатно
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className={s.faqSection}>
        <div className={s.faqLeft}>
          <h2>Ответы на вопросы</h2>
          <Link
            to="/register-clinic"
            className={`${s.btn} ${s.btnOutline}`}
            onClick={onTry}
          >
            Попробовать бесплатно
          </Link>
        </div>
        <div className={s.faqList}>
          {FAQ.map((item) => (
            <details key={item.q} className={s.faqItem}>
              <summary>
                <span>{item.q}</span>
                <i aria-hidden>+</i>
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className={s.footer}>
        <LegalFooterLinks />
      </footer>

      {!hasClub ? (
        <div className={s.sticky}>
          <Link
            to="/register-clinic"
            className={`${s.btn} ${s.btnDark}`}
            onClick={onTry}
          >
            {TRIAL_DAYS} дней бесплатно
          </Link>
        </div>
      ) : null}
    </div>
  );
}
